/**
 * WCAG-style luminance utilities for classifying colors as light or dark.
 *
 * Used by the colors swatch grid (product editor, Section 3) to overlay
 * appropriate contrast borders/labels on color swatches without per-color
 * manual tagging.
 *
 * Combined classifier (``classifyLDWithName``, task #170) folds the
 * distributor's manual override, the WCAG hex luminance, and the name-based
 * heuristic into a single call returning a rich ``{ ld, confidence }`` tuple.
 * This is the canonical entry point for swatch-only colors (no hex), since
 * the name heuristic is the only signal we have for them.
 */
import { classifyByName } from '@/utils/colorNameHeuristic'

const HEX_RE = /^[0-9a-fA-F]+$/;

/**
 * Parse a hex color string into an {r, g, b} object with channel values
 * in [0, 255]. Accepts '#RRGGBB', 'RRGGBB', '#RGB', or 'RGB'.
 *
 * Returns null for null, empty, or malformed input.
 */
export function hexToRgb(hex) {
  if (hex === null || hex === undefined) return null;
  if (typeof hex !== 'string') return null;

  let cleaned = hex.trim();
  if (cleaned.length === 0) return null;
  if (cleaned.startsWith('#')) cleaned = cleaned.slice(1);

  if (!HEX_RE.test(cleaned)) return null;

  let expanded;
  if (cleaned.length === 3) {
    expanded = cleaned
      .split('')
      .map((c) => c + c)
      .join('');
  } else if (cleaned.length === 6) {
    expanded = cleaned;
  } else {
    return null;
  }

  return {
    r: parseInt(expanded.slice(0, 2), 16),
    g: parseInt(expanded.slice(2, 4), 16),
    b: parseInt(expanded.slice(4, 6), 16),
  };
}

/**
 * Convert an 8-bit sRGB channel value [0,255] to linear-light [0,1] using
 * the WCAG 2.x piecewise gamma curve.
 */
function srgbChannelToLinear(c8) {
  const c = c8 / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/**
 * Compute WCAG relative luminance for a hex color string.
 * Returns a value in [0, 1] (0 = black, 1 = white), or null on invalid input.
 *
 *   L = 0.2126 * R_linear + 0.7152 * G_linear + 0.0722 * B_linear
 */
export function relativeLuminance(hex) {
  const rgb = hexToRgb(hex);
  if (rgb === null) return null;

  const rL = srgbChannelToLinear(rgb.r);
  const gL = srgbChannelToLinear(rgb.g);
  const bL = srgbChannelToLinear(rgb.b);

  return 0.2126 * rL + 0.7152 * gL + 0.0722 * bL;
}

/**
 * Classify a hex color as 'light' or 'dark' based on its relative luminance.
 * Returns null on invalid hex input.
 *
 * Default threshold of 0.179 tunes for SanMar's catalog: "Navy" classifies
 * as dark and "Athletic Heather" classifies as light.
 */
export function classifyLD(hex, threshold = 0.179) {
  const L = relativeLuminance(hex);
  if (L === null) return null;
  return L > threshold ? 'light' : 'dark';
}

/**
 * Pick a high-contrast ink color (black or white) for text/details
 * placed on top of a background of the given hex.
 *
 * Returns:
 *   - '#1a2332' (navy/near-black) for light backgrounds
 *   - '#ffffff' for dark backgrounds
 *   - '#1a2332' as a safe default when hex is null/invalid
 *
 * Used by the hoodie SVG modules to pick ribbing/stitch ink that
 * reads against the garment color.
 */
export function contrastInk(hex) {
  const cls = classifyLD(hex);
  return cls === 'dark' ? '#ffffff' : '#1a2332';
}

/**
 * Resolve a color's effective light/dark classification, folding a
 * distributor's manual override map over the auto-classify fallback.
 *
 * Promoted here (utils/luminance) from three inline copies that had
 * accumulated in the product editor. Today's call site is
 * ColorsPanel.vue (the swatch grid + filters); the other historical
 * copies (DecorationCard.vue, LivePreviewCard.vue) were deleted with
 * the legacy editor on Day 6.5. New code on the canonical editor
 * uses `classifyLDWithName` below.
 *
 * The map shape is ``{ <colorSku>: 'L' | 'D' | 'light' | 'dark' }`` — the
 * editor persists 'L' / 'D' as the override values, but historic call
 * sites have also stored 'light' / 'dark'. Both shapes are accepted and
 * normalized to the long-form returned by ``classifyLD``.
 *
 * @param {Record<string, string>|null|undefined} manualLdMap
 *   Map of colorSku → override. ``null`` / ``undefined`` means "no
 *   overrides recorded yet" — fall back to ``classifyLD`` for every sku.
 * @param {string} colorSku  The color's SKU (key into manualLdMap).
 * @param {string} hex       The color's hex value (fallback for classifyLD).
 * @returns {'light'|'dark'|null}
 *   The effective classification: manual override if present, else the
 *   WCAG auto-classification. ``null`` only when no override AND
 *   ``classifyLD(hex)`` itself returns null (invalid hex).
 *
 * @see classifyLDWithName for new code. This helper does NOT consult the
 *   name heuristic, so swatch-only colors (no hex) return ``null`` here
 *   and the caller's binary flip can route backwards. Prefer
 *   ``classifyLDWithName`` unless you specifically need the legacy
 *   2-arg manual-or-hex semantics.
 */
export function effectiveLD(manualLdMap, colorSku, hex) {
  if (
    manualLdMap !== null &&
    manualLdMap !== undefined &&
    Object.prototype.hasOwnProperty.call(manualLdMap, colorSku)
  ) {
    const override = manualLdMap[colorSku];
    if (override === 'L' || override === 'light') return 'light';
    if (override === 'D' || override === 'dark') return 'dark';
    // Unrecognized override value: fall through to auto.
  }
  return classifyLD(hex);
}

/**
 * Combined L/D classifier returning the rich tier shape.
 *
 * Priority (highest wins):
 *   1. Manual override — distributor explicitly set L or D for this sku.
 *      Returned as ``confidence: 'manual'``.
 *   2. Hex luminance — WCAG ``classifyLD`` on the supplier hex.
 *      Returned as ``confidence: 'auto'``.
 *   3. Name heuristic (high confidence) — saturated dictionary hit
 *      ("Black", "Navy", "Carolina") or explicit "Dark X" / "Light X"
 *      modifier. Returned as ``confidence: 'heuristic-high'``.
 *   4. Name heuristic (low confidence) — mid-tone word ("Heather", "Grey").
 *      Defaults to ``'dark'`` (industry convention) and returns
 *      ``confidence: 'heuristic-low'`` so callers can flag the guess.
 *   5. No signal — returns ``{ ld: null, confidence: 'ambiguous' }``.
 *
 * This is the canonical classifier for swatch-only colors (no hex). It is
 * also the path forward for hex-bearing colors so a single helper governs
 * the priority chain. Callers that don't care about confidence can read
 * ``.ld`` and ignore the rest; callers that surface the tier in the UI
 * (ColorsPanel pip rendering, ColorFilmstrip swatch contrast, VariantMenu /
 * VariantCard chips, StageHeader title contrast) keep the full tuple.
 *
 * Override-value validation: only ``'L'``, ``'light'``, ``'D'``, ``'dark'``
 * are treated as manual hits. Other values (corrupted seed data, future
 * bug, unrecognized string) fall through to the auto/heuristic chain.
 * This differs from ColorsPanel's legacy ``hasManualOverride`` which
 * treats ANY key presence as manual. When the wrapper is rewired,
 * normalize override values at write-time so the two definitions
 * converge.
 *
 * @param {{ hex?: string|null, name?: string|null, manualOverride?: string|null }} args
 * @returns {{ ld: 'light'|'dark'|null, confidence: 'manual'|'auto'|'heuristic-high'|'heuristic-low'|'ambiguous' }}
 */
export function classifyLDWithName({ hex, name, manualOverride } = {}) {
  if (manualOverride === 'L' || manualOverride === 'light') {
    return { ld: 'light', confidence: 'manual' }
  }
  if (manualOverride === 'D' || manualOverride === 'dark') {
    return { ld: 'dark', confidence: 'manual' }
  }
  const auto = classifyLD(hex)
  if (auto === 'light' || auto === 'dark') {
    return { ld: auto, confidence: 'auto' }
  }
  const heuristic = classifyByName(name)
  if (heuristic.ld === 'light' || heuristic.ld === 'dark') {
    const tier
      = heuristic.confidence === 'high'
        ? 'heuristic-high'
        : 'heuristic-low'
    return { ld: heuristic.ld, confidence: tier }
  }
  return { ld: null, confidence: 'ambiguous' }
}

/**
 * Compute WCAG relative luminance from an already-parsed {r, g, b} object
 * (channel values in [0, 255]). Private counterpart to ``relativeLuminance``
 * that avoids re-parsing hex when the caller already holds the rgb object.
 */
export function luminanceFromRgb({ r, g, b }) {
  return (
    0.2126 * srgbChannelToLinear(r) +
    0.7152 * srgbChannelToLinear(g) +
    0.0722 * srgbChannelToLinear(b)
  );
}

// Thresholds for classifyColor — declared at module level so they aren't
// re-created on every call.
const VIVID_SAT_MIN = 0.60; // minimum HSL saturation to qualify as vivid
const VIVID_L_MIN   = 0.15; // HSL lightness lower bound for vivid band
const VIVID_L_MAX   = 0.85; // HSL lightness upper bound for vivid band
const LIGHT_LUM_MIN = 0.40; // WCAG luminance above which color is 'light'
const DARK_LUM_MAX  = 0.10; // WCAG luminance at or below which color is 'dark'

/**
 * Convert an {r, g, b} object (channel values in [0, 255]) to HSL.
 * Returns { h: [0,360), s: [0,1], l: [0,1] }.
 */
function rgbToHSL({ r, g, b }) {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;

  if (max === min) {
    return { h: 0, s: 0, l };
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h;
  if (max === rn) {
    h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
  } else if (max === gn) {
    h = ((bn - rn) / d + 2) / 6;
  } else {
    h = ((rn - gn) / d + 4) / 6;
  }

  return { h: h * 360, s, l };
}

/**
 * Classify a hex color into one of four perceptual tiers:
 *
 *   'light'  — high luminance (WCAG relative luminance > 0.40)
 *   'dark'   — low luminance  (WCAG relative luminance ≤ 0.10)
 *   'vivid'  — strongly saturated mid-tone (HSL saturation ≥ 0.60 AND
 *              HSL lightness in [0.15, 0.85]). These are chromatically
 *              extreme colors that resist simple light/dark ink selection.
 *   'medium' — everything else (desaturated mid-tones)
 *
 * Evaluation order: vivid check runs BEFORE luminance thresholds so that
 * saturated primaries like pure blue (#0000ff, WCAG lum ≈ 0.072) are
 * classified as vivid rather than dark. Light/dark anchors with incidental
 * saturation (e.g., lemon chiffon, SanMar Navy) fall through to the
 * luminance tier because their HSL lightness is outside the vivid band.
 *
 * Returns null for null or invalid hex input.
 *
 * Thresholds are declared as module-level constants above this function
 * (VIVID_SAT_MIN, VIVID_L_MIN, VIVID_L_MAX, LIGHT_LUM_MIN, DARK_LUM_MAX).
 *
 * @param {string} hex  Hex color string ('#RRGGBB', 'RRGGBB', '#RGB', 'RGB').
 * @returns {'light'|'medium'|'dark'|'vivid'|null}
 */
export function classifyColor(hex) {
  const rgb = hexToRgb(hex);
  if (rgb === null) return null;

  const { s: hslS, l: hslL } = rgbToHSL(rgb);

  // Vivid check: strongly chromatic mid-tones — run before luminance so
  // saturated primaries (e.g., pure blue) aren't absorbed into 'dark'.
  if (hslS >= VIVID_SAT_MIN && hslL >= VIVID_L_MIN && hslL <= VIVID_L_MAX) {
    return 'vivid';
  }

  // Luminance tiers — reuse the already-parsed rgb to avoid a second hex parse.
  const lum = luminanceFromRgb(rgb);
  if (lum > LIGHT_LUM_MIN) return 'light';
  if (lum <= DARK_LUM_MAX)  return 'dark';
  return 'medium';
}

/**
 * Compute WCAG contrast ratio between two hex colors.
 *
 * Formula:
 *   ratio = (L1 + 0.05) / (L2 + 0.05)
 * where L1 is the higher relative luminance and L2 is the lower.
 *
 * Returns:
 *   - Number in range [1.0, 21.0]: 1.0 = identical, 21.0 = black vs white
 *   - null when either input fails relativeLuminance (invalid hex)
 *
 * Reference: WCAG 2.x §1.4.11 (non-text contrast — 3:1 minimum for
 * meaningful UI graphics; we apply this to artwork-vs-garment-color).
 */
export function contrastRatio(hexA, hexB) {
  const La = relativeLuminance(hexA);
  const Lb = relativeLuminance(hexB);
  if (La === null || Lb === null) return null;
  const hi = La >= Lb ? La : Lb;
  const lo = La >= Lb ? Lb : La;
  return (hi + 0.05) / (lo + 0.05);
}
